import './NotificationPanel.scss'
import NotificationComp from '../NotificationComp/NotificationComp';
import NotificationSort from '../NotificationSortComp/NotificationSort';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseBtn from '../../../CommonComponents/ModalButtons/ModalCloseBtn/ModalCloseBtn';
import { NotificationAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/NotificationAPIHandler';
import { NotificationInterface } from '../../../../Models/Notification';
import { useDispatch, useSelector } from 'react-redux';
import { notifyPanelClosed, resetNewNotification } from './../NotificationSlice';
import { RootState } from '../../../../redux/store';
import { ErrorLogger } from '../../../../utils/Logger';

interface NotificationProps {
  openPanel: boolean,
  setOpenPanel: any
}

const NotificationPanel = (props: NotificationProps) => {
  const { openPanel, setOpenPanel } = props
  const [notificationData, setNotificationData] = useState<NotificationInterface[] | []>([])
  const [notificationFilterData, setNotificationFilterData] = useState<NotificationInterface[] | []>([])
  const [scrollPosition, setScrollPosition] = useState(0)
  const [showMobileSort, setShowMobileSort] = useState(false)

  const dispatch = useDispatch();
  const newNotificationReceived = useSelector((state: RootState) => state.notification.newNotificationReceived);

  useEffect(() => {
    if (newNotificationReceived && openPanel) {
      fetchNotifications();
      dispatch(resetNewNotification());
    }
    // eslint-disable-next-line 
  }, [newNotificationReceived, openPanel]);
  
  const closeNotificationPanel = async () => {
    setShowMobileSort(false);
    setOpenPanel(false);
    try {
      await NotificationAPIHandler.shared.markAllNotificationsAsRead();
    } catch (err) {
      ErrorLogger.log(err, 'NotificationPanel.closeNotificationPanel');
    } finally {
      dispatch(notifyPanelClosed());
    }
  };

  const checkIsMobile = () => {
    return window.innerWidth <= 1023; // Match navbar breakpoint - includes Galaxy Fold opened
  };

  const disableScroll = () => {
    const currentPosition = window.scrollY;
    setScrollPosition(currentPosition);
    
    if (checkIsMobile()) {
      // On mobile: prevent body scroll completely
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentPosition}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      // Prevent overscroll bounce
      document.body.style.overscrollBehavior = "none";
    } else {
      // On desktop: use existing behavior
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentPosition}px`;
    }
  };

  const enableScroll = () => {
    if (checkIsMobile()) {
      // On mobile: restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    } else {
      // On desktop: use existing behavior
      document.body.style.position = "static";
      window.scrollTo(0, scrollPosition);
    }
  };

  // Handle scroll event to prevent background scrolling when panel reaches boundaries
  const handlePanelScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!checkIsMobile()) return;
    
    const target = e.currentTarget;
    const isAtTop = target.scrollTop === 0;
    const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;
    
    // Prevent scroll propagation when at boundaries
    if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Handle touch to prevent scroll chaining
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!checkIsMobile()) return;
    
    const target = e.currentTarget;
    const isAtTop = target.scrollTop === 0;
    const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;
    
    // If at boundaries, prevent default to stop scroll chaining
    if (isAtTop || isAtBottom) {
      // Allow scrolling within the panel, but prevent when at boundaries
      const touch = e.touches[0];
      if (touch) {
        const scrollDirection = (e as any).lastTouchY 
          ? touch.clientY - (e as any).lastTouchY 
          : 0;
        (e as any).lastTouchY = touch.clientY;
        
        // Prevent if trying to scroll past boundaries
        if ((isAtTop && scrollDirection > 0) || (isAtBottom && scrollDirection < 0)) {
          e.preventDefault();
        }
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await NotificationAPIHandler.shared.getNotificationsForUser();
      setNotificationData(response.allNotifications);
      setNotificationFilterData(response.allNotifications);
    } catch (error) {
      ErrorLogger.log(error, 'NotificationPanel.fetchNotifications');
    }
  };

  const toggleMobileSort = () => {
    setShowMobileSort(!showMobileSort);
  };

  const closeMobileSort = () => {
    setShowMobileSort(false);
  };

  useEffect(() => {
    if(openPanel) {
      disableScroll()
      fetchNotifications();
    } else {
      enableScroll()
      setShowMobileSort(false);
    }
    // eslint-disable-next-line
  }, [openPanel])

  // Handle dynamic viewport height changes (e.g., when address bar shows/hides)
  useEffect(() => {
    if (!openPanel || !checkIsMobile()) return;

    const updateHeight = () => {
      const panel = document.querySelector('.notificationPanel.active') as HTMLElement;
      if (panel) {
        const newHeight = `calc(100dvh - 60px)`;
        panel.style.height = newHeight;
        panel.style.minHeight = newHeight;
        panel.style.maxHeight = newHeight;
      }
    };

    // Update on visual viewport resize (handles address bar show/hide)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateHeight);
      window.visualViewport.addEventListener('scroll', updateHeight);
    }

    // Fallback for browsers without visualViewport
    window.addEventListener('resize', updateHeight);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateHeight);
        window.visualViewport.removeEventListener('scroll', updateHeight);
      }
      window.removeEventListener('resize', updateHeight);
    };
  }, [openPanel])

  const notificationPanelContent = (
    <div 
      className={openPanel ? 'notificationPanel active' : 'notificationPanel'} 
      style={{visibility: openPanel ? 'visible' : 'hidden'}}
    >
        <div className="notificationPanelHead">
          <div className="left">
            <NotificationsIcon />
            <p>Notifications</p>
          </div>
          <div className="right">
            <ModalCloseBtn handleCancel={closeNotificationPanel} />
          </div>
        </div>
        <div 
          className="notificationPanelBody"
          onWheel={handlePanelScroll}
          onTouchMove={handleTouchMove}
        >
          {/* Desktop Sort Panel */}
          <div className="desktop-sort-panel">
            <NotificationSort data={notificationData} setFilterData={setNotificationFilterData} openPanel={openPanel} />
          </div>
          
          {/* Notifications List */}
          <NotificationComp openPanel={openPanel} notificationData={notificationFilterData} />
          
          {/* Mobile Sort Panel */}
          <div className={`mobile-sort-panel ${showMobileSort ? 'active' : ''}`} style={{ visibility: showMobileSort ? 'visible' : 'hidden' }}>
            <div className="mobile-sort-header">
              <h3>Sort & Filter</h3>
              <button className="close-sort-btn" onClick={closeMobileSort}>
                <CloseIcon />
              </button>
            </div>
            <NotificationSort data={notificationData} setFilterData={setNotificationFilterData} openPanel={openPanel} />
          </div>
        </div>
        
        {/* Floating Action Button */}
        <div className="mobile-fab" onClick={toggleMobileSort}>
          <FilterListIcon />
        </div>
    </div>
  );

  // Use portal on mobile to render outside of any stacking context
  if (checkIsMobile() && typeof document !== 'undefined') {
    return createPortal(notificationPanelContent, document.body);
  }

  return notificationPanelContent;
}

export default NotificationPanel